import { cn } from '~/lib/utils'
import { Markdown } from '~/components/ui/markdown'
import { FeedbackButtons } from '~/components/shared/feedback-buttons'
import type { ChatMessage } from '~/hooks/use-chat-stream'
import type { FeedbackService } from '~/api/types'

export interface MessageBubbleProps {
    message: ChatMessage
    /** 평점 위젯을 붙일 서비스 프로파일. 주면 비어있지 않은 assistant 답변에 표시한다. */
    feedbackService?: FeedbackService
    /** 평점 대상 id(세션 id 등). 없으면 생략(백엔드 nullable). */
    feedbackTargetId?: string
}

/** 대화 메시지 버블. user는 우측 accent, assistant는 좌측 surface(마크다운 렌더). */
export function MessageBubble({
    message,
    feedbackService,
    feedbackTargetId,
}: MessageBubbleProps) {
    const isUser = message.role === 'user'
    // 스트리밍 중 아직 토큰이 없는 빈 assistant 버블은 그리지 않는다.
    // (StreamingIndicator가 '근거를 살펴보는 중…'을 이미 표시 — 빈 '…' 버블 중복 제거)
    if (!isUser && message.streaming && !message.content) return null
    // 답변당 1개: streaming이 끝났고 내용이 있는 assistant 답변에만 평점을 단다.
    const showFeedback =
        !isUser && !message.streaming && !!message.content && !!feedbackService

    return (
        <div
            className={cn(
                'flex w-full',
                isUser ? 'justify-end' : 'justify-start'
            )}>
            <div
                className={cn(
                    'flex max-w-[80%] min-w-0 flex-col',
                    isUser ? 'items-end' : 'items-start'
                )}>
                <div
                    className={cn(
                        'min-w-0 rounded-md px-3.5 py-2.5 text-sm leading-relaxed',
                        isUser
                            ? 'bg-primary text-primary-foreground whitespace-pre-wrap'
                            : 'border-border bg-card text-foreground border'
                    )}>
                    {message.content ? (
                        isUser ? (
                            message.content
                        ) : (
                            <Markdown>{message.content}</Markdown>
                        )
                    ) : message.streaming ? (
                        <span className='text-muted-foreground'>…</span>
                    ) : null}
                </div>
                {showFeedback ? (
                    <FeedbackButtons
                        service={feedbackService}
                        targetType='message'
                        targetId={feedbackTargetId ?? message.id}
                        className='mt-1.5 px-1'
                    />
                ) : null}
            </div>
        </div>
    )
}
